import { UseDisclosureReturn } from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { useCallback } from "react"
import { matchPath } from "react-router-dom"
import { AugmentationToolbarHookReturn } from "./AugmentationToolbar"
import { useQueryParamState } from "./useQueryParamHistory"

/** Marks a section's open "Add …" picker, which owns Escape while open. */
export const AUGMENTATION_PANEL_ADD_FORM_CLASS =
  "AugmentationPanelSectionAddForm"

function splitPathLocation(path: string) {
  const hashIndex = path.indexOf("#")
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : ""
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path
  const searchIndex = withoutHash.indexOf("?")
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : ""
  const pathname =
    searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash

  return { pathname, search, hash }
}

/** Find the nearest ancestor destination, skipping route groups without content. */
export function getParentPanelPathForStack(
  path: string,
  stack: AugmentationToolbarHookReturn["stack"]
): string | null {
  const { pathname, search, hash } = splitPathLocation(path)
  let routePaths = [""]
  let parentPath: string | null = null

  for (const option of stack.slice(0, -1)) {
    const paths = Array.isArray(option.path)
      ? option.path
      : [option.path || option.key]
    routePaths = routePaths.flatMap((basePath) =>
      paths.map((path) => (basePath ? `${basePath}/${path}` : `/${path}`))
    )
    if (!option.AugmentationComponent) continue

    // Match the actual route so aliases, parameters, and multi-segment paths
    // return to the same ancestor destination as the panel router.
    const match = routePaths
      .map((path) => matchPath(pathname, { path }))
      .filter((match) => match !== null)
      .sort((a, b) => b.url.length - a.url.length)[0]
    if (match) parentPath = `${match.url}${search}${hash}`
  }

  return parentPath
}

type UseAugmentationPanelEscapeProps = {
  toolbar: AugmentationToolbarHookReturn
  disclosure?: UseDisclosureReturn
  queryParamKey: string
  hasOpenDialog?: boolean
}

function claimEscapeEvent(e: KeyboardEvent) {
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()
}

function useAugmentationPanelEscape({
  toolbar,
  disclosure,
  queryParamKey,
  hasOpenDialog
}: UseAugmentationPanelEscapeProps) {
  const { queryParamPath, setQueryParamPath } =
    useQueryParamState(queryParamKey)

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.defaultPrevented) return false
      if (!disclosure?.open) return false
      if (hasOpenDialog) return false
      // An open "Add …" picker handles its own Escape: it cancels the picker
      // and leaves the panel open.
      if (
        e.target instanceof Element &&
        e.target.closest(`.${AUGMENTATION_PANEL_ADD_FORM_CLASS}`)
      )
        return false

      const parentPath = queryParamPath
        ? getParentPanelPathForStack(queryParamPath, toolbar.stack)
        : null

      if (parentPath) {
        claimEscapeEvent(e)
        setQueryParamPath(parentPath)
        return
      }

      claimEscapeEvent(e)
      disclosure.onClose()
    },
    [
      disclosure,
      hasOpenDialog,
      queryParamPath,
      setQueryParamPath,
      toolbar.stack
    ]
  )

  useKeyControl("Escape", handleEscape)

  return handleEscape
}

export default useAugmentationPanelEscape
